// ANGEL FLIGHT — HangarScreen (the Armory, DOM). The store actually SELLS —
// and now actually EXPLAINS.
//
// Rebuilt from the audited single 640px scroll column (everything clumped in
// the middle, white space both sides) into a WIDE command layout:
//   • HEADER BAND — ARMORY title left; big SCRIP/SALVAGE wallet chips right
//     with the one-line economy truth: "earned per kill · banked win or lose".
//   • "YOUR BIRD" LOADOUT STRIP — five labeled stat cells (HULL / DAMAGE /
//     FIRE RATE / BELT / VENT) resolved from the live save. They repaint the
//     instant a tier is bought — visible proof the upgrades actually work.
//   • TWO COLUMNS on wide screens (WEAPONS | UPGRADES), stacking on narrow —
//     the side white space now holds the store.
// Upgrade rows show the REAL effect numbers (NOW ×1.15 → NEXT ×1.32) and both
// prices (SCRIP buy / SALVAGE retrofit). All purchases persist via writeSave so
// the next run reflects them; prices come from config (no magic numbers).

import type { Screen } from '../Router';
import type { SaveStateV1, UpgradeDef, UpgradeId, UpgradeTier, WeaponId } from '../../core/types';
import { writeSave } from '../../core/storage';
import { BALANCE } from '../../config/balance';
import { getDifficulty } from '../../config/difficulty';
import {
  UPGRADES,
  UPGRADE_LIST,
  maxTier,
  nextTierCost,
  salvageTierCost,
  resolvePlayerStats,
} from '../../config/upgrades';
import { WEAPONS, WEAPON_SLOTS, weaponPrice } from '../../config/weapons';
import {
  FLIR,
  FONT,
  makeButton,
  makePanel,
  makeScreenRoot,
  el,
  type ButtonHandle,
} from './ui';

/** One-line identity blurb per gun (the catalog keeps stats; this is voice). */
const WEAPON_BLURBS: Readonly<Record<WeaponId, string>> = Object.freeze({
  minigun: 'The hose. Fast, forgiving, always spinning.',
  autocannon: '30mm HE — slow thump, splash on impact.',
  railgun: 'One lance, one line. Pierces the whole column.',
  flamer: 'Short-range cone. Crowd control at the wire.',
});

/** Shared content width — the layout fills the frame instead of a thin column. */
const WIDE = 'min(1100px, 94vw)';

/** How each upgrade's tier value reads as a player-facing effect. */
const EFFECT_FMT: Readonly<Record<UpgradeId, (v: number) => string>> = Object.freeze({
  damage: (v) => `×${v.toFixed(2)} DMG`,
  fireRate: (v) => `×${v.toFixed(2)} RATE`,
  ammoCap: (v) => `+${v} BELT`,
  reload: (v) => `×${v.toFixed(2)} VENT`,
  hull: (v) => `${v} HULL`,
  autoRepair: (v) => `+${v} HP/S`,
});

/** The tier-0 baseline each effect grows from. */
const EFFECT_BASE: Readonly<Record<UpgradeId, number>> = Object.freeze({
  damage: 1,
  fireRate: 1,
  ammoCap: 0,
  reload: 1,
  hull: BALANCE.player.baseHull,
  autoRepair: 0,
});

/** The upgrade's effect value at a tier (tier 0 = the baseline). */
function effectAt(id: UpgradeId, tier: number): number {
  if (tier <= 0) return EFFECT_BASE[id];
  return UPGRADES[id].values[tier - 1] ?? EFFECT_BASE[id];
}

export interface HangarScreenOptions {
  /** Live save — mutated + persisted on purchase. */
  save: SaveStateV1;
  /** Return to the menu. */
  onBack: () => void;
}

export class HangarScreen implements Screen<void> {
  private readonly opts: HangarScreenOptions;
  private root: HTMLDivElement | null = null;
  private scripChipVal: HTMLDivElement | null = null;
  private salvChipVal: HTMLDivElement | null = null;
  private readonly statCells = new Map<string, HTMLDivElement>();
  private backBtn: ButtonHandle | null = null;
  /** Per-row buy/equip buttons (destroyed + rebuilt each render). */
  private buyButtons: ButtonHandle[] = [];
  private weaponList: HTMLElement | null = null;
  private upgradeList: HTMLElement | null = null;

  constructor(opts: HangarScreenOptions) {
    this.opts = opts;
  }

  mount(parent: HTMLElement): void {
    const root = makeScreenRoot({ center: false });
    root.style.gap = '14px';
    root.style.overflowY = 'auto';

    // ── HEADER BAND: title left, wallet right — the economy, stated ─────────
    const headerBar = el('div', {
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: '14px',
      width: WIDE,
    });
    const titleCol = el('div', { display: 'flex', flexDirection: 'column', gap: '4px' });
    titleCol.append(
      el(
        'div',
        { font: `700 clamp(26px, 5vw, 38px)/1 ${FONT}`, letterSpacing: '0.08em', color: FLIR.green },
        'ARMORY',
      ),
      el(
        'div',
        { font: `500 clamp(11px, 2vw, 13px)/1.3 ${FONT}`, letterSpacing: '0.14em', color: FLIR.greenDim },
        'GEAR UP BETWEEN SORTIES',
      ),
    );
    const walletCol = el('div', {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: '6px',
    });
    const chips = el('div', { display: 'flex', gap: '10px' });
    const scripChip = this.makeWalletChip('SCRIP', FLIR.accent);
    const salvChip = this.makeWalletChip('SALVAGE', FLIR.green);
    this.scripChipVal = scripChip.value;
    this.salvChipVal = salvChip.value;
    chips.append(scripChip.el, salvChip.el);
    walletCol.append(
      chips,
      el(
        'div',
        { font: `500 clamp(10px, 1.8vw, 12px)/1.3 ${FONT}`, letterSpacing: '0.1em', color: FLIR.greenDim, textAlign: 'right' },
        'EARNED PER KILL · BANKED WIN OR LOSE',
      ),
    );
    headerBar.append(titleCol, walletCol);

    // ── "YOUR BIRD" LOADOUT STRIP: the live resolved stats, labeled ─────────
    const loadout = makePanel({
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
      gap: '12px',
      width: WIDE,
      padding: '12px 18px',
    });
    const loadoutHead = el(
      'div',
      { font: `700 11px/1 ${FONT}`, letterSpacing: '0.1em', color: FLIR.greenDim, gridColumn: '1 / -1' },
      'YOUR BIRD',
    );
    loadout.append(loadoutHead);
    for (const key of ['HULL', 'DAMAGE', 'FIRE RATE', 'BELT', 'VENT', 'REPAIR'] as const) {
      const cell = el('div', { display: 'flex', flexDirection: 'column', gap: '3px' });
      const value = el('div', {
        font: `800 clamp(16px, 3vw, 21px)/1 ${FONT}`,
        letterSpacing: '0.06em',
        color: FLIR.green,
      });
      cell.append(
        el('div', { font: `600 10px/1 ${FONT}`, letterSpacing: '0.18em', color: FLIR.greenDim }, key),
        value,
      );
      this.statCells.set(key, value);
      loadout.append(cell);
    }

    // ── TWO STORE COLUMNS (stacking on narrow screens) ──────────────────────
    const columns = el('div', {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(440px, 92vw), 1fr))',
      gap: '18px',
      width: WIDE,
      alignItems: 'start',
    });
    const weaponsCol = el('div', { display: 'flex', flexDirection: 'column', gap: '10px' });
    weaponsCol.append(this.sectionHeader('WEAPONS · SLOTS 1–4'));
    this.weaponList = el('div', { display: 'flex', flexDirection: 'column', gap: '10px' });
    weaponsCol.append(this.weaponList);
    const upgradesCol = el('div', { display: 'flex', flexDirection: 'column', gap: '10px' });
    upgradesCol.append(this.sectionHeader('UPGRADES'));
    this.upgradeList = el('div', { display: 'flex', flexDirection: 'column', gap: '10px' });
    upgradesCol.append(this.upgradeList);
    columns.append(weaponsCol, upgradesCol);

    root.append(headerBar, loadout, columns);

    this.backBtn = makeButton('BACK', () => this.opts.onBack());
    this.backBtn.el.style.margin = '12px 0 8px';
    root.append(this.backBtn.el);

    parent.appendChild(root);
    this.root = root;

    this.renderRows();
    this.refreshHeader();
  }

  /** A wallet chip: a boxed currency readout (label small, count big). */
  private makeWalletChip(label: string, color: string): { el: HTMLDivElement; value: HTMLDivElement } {
    const chip = el('div', {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: '3px',
      background: FLIR.panel,
      border: `1px solid ${color}`,
      borderRadius: '8px',
      padding: '8px 14px',
      minWidth: '96px',
    });
    const value = el('div', {
      font: `800 clamp(18px, 3.4vw, 24px)/1 ${FONT}`,
      letterSpacing: '0.04em',
      color,
    });
    chip.append(
      el('div', { font: `600 10px/1 ${FONT}`, letterSpacing: '0.18em', color: FLIR.greenDim }, label),
      value,
    );
    return { el: chip, value };
  }

  private sectionHeader(text: string): HTMLDivElement {
    return el(
      'div',
      {
        font: `700 clamp(12px, 2.2vw, 14px)/1 ${FONT}`,
        letterSpacing: '0.1em',
        color: FLIR.greenDim,
        marginTop: '4px',
      },
      text,
    );
  }

  private refreshHeader(): void {
    const { save } = this.opts;
    if (this.scripChipVal) this.scripChipVal.textContent = `${save.scrip}`;
    if (this.salvChipVal) this.salvChipVal.textContent = `${save.salvage}`;
    const stats = resolvePlayerStats(save, getDifficulty(save.difficulty));
    const cells: ReadonlyArray<readonly [string, string]> = [
      ['HULL', `${stats.maxHull}`],
      ['DAMAGE', `×${stats.damageMult.toFixed(2)}`],
      ['FIRE RATE', `×${stats.fireRateMult.toFixed(2)}`],
      ['BELT', `+${stats.ammoCap}`],
      ['VENT', `×${stats.reloadMult.toFixed(2)}`],
      ['REPAIR', `+${stats.autoRepair}/S`],
    ];
    for (const [key, value] of cells) {
      const cell = this.statCells.get(key);
      if (cell) cell.textContent = value;
    }
  }

  private renderRows(): void {
    for (const b of this.buyButtons) b.destroy();
    this.buyButtons = [];
    if (this.weaponList) {
      this.weaponList.replaceChildren();
      WEAPON_SLOTS.forEach((id, i) => this.weaponList?.appendChild(this.makeWeaponRow(id, i + 1)));
    }
    if (this.upgradeList) {
      this.upgradeList.replaceChildren();
      for (const def of UPGRADE_LIST) this.upgradeList.appendChild(this.makeUpgradeRow(def));
    }
  }

  // ── WEAPONS ───────────────────────────────────────────────────────────────

  private makeWeaponRow(id: WeaponId, slot: number): HTMLDivElement {
    const { save } = this.opts;
    const def = WEAPONS[id];
    const owned = save.ownedWeapons.includes(id);
    const equipped = save.equippedWeapon === id;
    const price = weaponPrice(id);

    const row = makePanel({
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
      padding: '14px 16px',
    });
    if (equipped) row.style.border = `1px solid ${FLIR.accent}`;

    // Slot digit chip — matches the keyboard key + the in-game touch button.
    const slotChip = el(
      'div',
      {
        font: `800 clamp(16px, 3vw, 20px)/1 ${FONT}`,
        color: owned ? FLIR.accent : FLIR.greenFaint,
        border: `1px solid ${owned ? FLIR.accent : FLIR.greenFaint}`,
        borderRadius: '6px',
        padding: '8px 11px',
        flex: '0 0 auto',
      },
      `${slot}`,
    );

    const info = el('div', { flex: '1 1 auto', minWidth: '0' });
    const name = el(
      'div',
      {
        font: `700 clamp(14px, 2.6vw, 17px)/1.2 ${FONT}`,
        letterSpacing: '0.1em',
        color: owned ? FLIR.green : FLIR.greenDim,
      },
      def.name.toUpperCase(),
    );
    const blurb = el(
      'div',
      {
        font: `500 clamp(11px, 2vw, 13px)/1.35 ${FONT}`,
        letterSpacing: '0.06em',
        color: FLIR.greenDim,
        marginTop: '4px',
      },
      WEAPON_BLURBS[id],
    );
    info.append(name, blurb);

    // Right: EQUIPPED / EQUIP / price.
    let action: HTMLElement;
    if (equipped) {
      action = el(
        'div',
        {
          font: `700 clamp(12px, 2.2vw, 15px)/1 ${FONT}`,
          letterSpacing: '0.14em',
          color: FLIR.accent,
          minWidth: '110px',
          textAlign: 'center',
          flex: '0 0 auto',
        },
        'EQUIPPED',
      );
    } else if (owned) {
      const equip = makeButton('EQUIP', () => this.equipWeapon(id), {
        width: '116px',
        minHeight: '48px',
      });
      equip.el.style.flex = '0 0 auto';
      this.buyButtons.push(equip);
      action = equip.el;
    } else {
      const buy = makeButton(`${price} SCRIP`, () => this.buyWeapon(id), {
        width: '116px',
        minHeight: '48px',
        enabled: save.scrip >= price,
        accent: save.scrip >= price,
      });
      buy.el.style.flex = '0 0 auto';
      this.buyButtons.push(buy);
      action = buy.el;
    }

    row.append(slotChip, info, action);
    return row;
  }

  private buyWeapon(id: WeaponId): void {
    const { save } = this.opts;
    const price = weaponPrice(id);
    if (save.ownedWeapons.includes(id) || save.scrip < price) return;
    save.scrip -= price;
    save.ownedWeapons.push(id);
    save.equippedWeapon = id; // fresh gun goes straight in your hands
    writeSave(save);
    this.renderRows();
    this.refreshHeader();
  }

  private equipWeapon(id: WeaponId): void {
    const { save } = this.opts;
    if (!save.ownedWeapons.includes(id)) return;
    save.equippedWeapon = id;
    writeSave(save);
    this.renderRows();
  }

  // ── UPGRADES ──────────────────────────────────────────────────────────────

  private makeUpgradeRow(def: UpgradeDef): HTMLDivElement {
    const { save } = this.opts;
    const owned = save.upgrades[def.id];
    const max = maxTier(def.id);
    const cost = nextTierCost(def.id, owned);
    const salvageCost = salvageTierCost(def.id, owned);

    const row = makePanel({
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
      padding: '14px 16px',
    });

    // Left: name + tag + blurb + the REAL effect numbers (now → next).
    const info = el('div', { flex: '1 1 auto', minWidth: '0' });
    const nameRow = el('div', { display: 'flex', alignItems: 'baseline', gap: '8px' });
    const tag = def.branch === 'gun' ? 'GUN' : 'BIRD';
    const name = el(
      'div',
      { font: `700 clamp(14px, 2.6vw, 17px)/1.2 ${FONT}`, letterSpacing: '0.1em', color: FLIR.green },
      def.name,
    );
    const branch = el(
      'div',
      {
        font: `700 11px/1 ${FONT}`,
        letterSpacing: '0.14em',
        color: FLIR.greenDim,
        border: `1px solid ${FLIR.greenFaint}`,
        borderRadius: '4px',
        padding: '2px 6px',
      },
      tag,
    );
    nameRow.append(name, branch);
    const blurb = el(
      'div',
      {
        font: `500 clamp(11px, 2vw, 13px)/1.35 ${FONT}`,
        letterSpacing: '0.06em',
        color: FLIR.greenDim,
        marginTop: '4px',
      },
      def.blurb,
    );
    // NOW ×1.15 → NEXT ×1.32 (or NOW ×1.85 · MAXED): the proof of purchase.
    const fmt = EFFECT_FMT[def.id];
    const effectLine = el('div', { display: 'flex', gap: '8px', marginTop: '5px', alignItems: 'baseline' });
    effectLine.append(
      el(
        'div',
        { font: `600 clamp(10px, 1.9vw, 12px)/1 ${FONT}`, letterSpacing: '0.1em', color: FLIR.greenDim },
        `NOW ${fmt(effectAt(def.id, owned))}`,
      ),
    );
    if (cost !== null) {
      effectLine.append(
        el(
          'div',
          { font: `700 clamp(10px, 1.9vw, 12px)/1 ${FONT}`, letterSpacing: '0.1em', color: FLIR.accent },
          `→ NEXT ${fmt(effectAt(def.id, owned + 1))}`,
        ),
      );
    } else {
      effectLine.append(
        el(
          'div',
          { font: `700 clamp(10px, 1.9vw, 12px)/1 ${FONT}`, letterSpacing: '0.14em', color: FLIR.accent },
          '· MAXED',
        ),
      );
    }
    info.append(nameRow, blurb, effectLine);

    // Middle: tier pips.
    const pips = el('div', { display: 'flex', gap: '6px', flex: '0 0 auto' });
    for (let t = 0; t < max; t++) {
      pips.appendChild(
        el('div', {
          width: '10px',
          height: '10px',
          borderRadius: '50%',
          background: t < owned ? FLIR.accent : 'transparent',
          border: `1px solid ${t < owned ? FLIR.accent : FLIR.greenFaint}`,
          boxShadow: t < owned ? '0 0 6px rgba(255,208,138,0.6)' : 'none',
        }),
      );
    }

    // Right: SCRIP buy + SALVAGE retrofit (or nothing — MAXED rides the
    // effect line, so a maxed row doesn't reserve dead button space).
    let buyEl: HTMLElement | null = null;
    if (cost !== null && salvageCost !== null) {
      const buyCol = el('div', {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        flex: '0 0 auto',
      });
      const buy = makeButton(
        `${cost} SCRIP`,
        () => this.buyUpgrade(def.id, 'scrip'),
        { width: '120px', minHeight: '44px', enabled: save.scrip >= cost },
      );
      const retro = makeButton(
        `${salvageCost} SALV`,
        () => this.buyUpgrade(def.id, 'salvage'),
        { width: '120px', minHeight: '44px', enabled: save.salvage >= salvageCost },
      );
      this.buyButtons.push(buy, retro);
      buyCol.append(buy.el, retro.el);
      buyEl = buyCol;
    }

    row.append(info, pips);
    if (buyEl) row.append(buyEl);
    return row;
  }

  private buyUpgrade(id: UpgradeId, wallet: 'scrip' | 'salvage'): void {
    const { save } = this.opts;
    const owned = save.upgrades[id];
    if (wallet === 'scrip') {
      const cost = nextTierCost(id, owned);
      if (cost === null || save.scrip < cost) return;
      save.scrip -= cost;
    } else {
      const cost = salvageTierCost(id, owned);
      if (cost === null || save.salvage < cost) return;
      save.salvage -= cost;
    }
    save.upgrades[id] = (owned + 1) as UpgradeTier;
    writeSave(save);
    this.renderRows();
    this.refreshHeader();
  }

  unmount(): void {
    for (const b of this.buyButtons) b.destroy();
    this.buyButtons = [];
    this.backBtn?.destroy();
    this.backBtn = null;
    this.root?.remove();
    this.root = null;
    this.scripChipVal = null;
    this.salvChipVal = null;
    this.statCells.clear();
    this.weaponList = null;
    this.upgradeList = null;
  }
}
